import React from 'react';
import R, {
  equals,
  cond,
  anyPass,
  always,
  prop,
  propEq,
  both,
  has,
  T,
  compose,
  ifElse,
  identity,
  path,
  map,
  } from 'ramda';
import { FontIcon } from 'material-ui';
import RatesReceivedIcon from 'material-ui/svg-icons/action/description';
import SentIcon from 'material-ui/svg-icons/content/send';
import LockIcon from 'material-ui/svg-icons/action/lock';
import RequestIcon from 'material-ui/svg-icons/content/content-paste';
import RCICon from 'material-ui/svg-icons/editor/highlight';
import ArrowUpward from 'material-ui/svg-icons/navigation/arrow-upward';
import ArrowDown from 'material-ui/svg-icons/navigation/arrow-downward';

import logoGlobeSrc from '../../../www/images/tourconnect-logo-globe.svg';
import WallWrapper from '../wall/wrapper';

const originFromMap = {
  multiLocSinglePartner: true,
  multiLocMultiPartners: true,
  multiLocRes: true,
  singleLocStarter: true,
};

const locationIdExists = compose(val => identity(val), prop('locationId'));

const transformSupplierState = cond([
  [contract => !contract.locationId && !contract.buyerChecked, always('request')],
  [contract => contract.state === 'pending', always('sent')],
  [T, prop('state')],
]);

const separatePendingForBuyer = cond([
  [locationIdExists, always('received')],
  [obj => prop('buyerChecked', obj), always('received')],
  [T, always('sent')],
]);
const transformBuyerState = contract => {
  if (contract.locationId) {
    if (contract.state === 'pending') return separatePendingForBuyer(contract);
    return contract.state;
  }
  if (contract.rateConversionStatus === 'in progress' || contract.rateConversionStatus === 'complete') {
    if (contract.lastSent) {
      if (contract.buyerChecked) return 'received';
      return 'sent';
    }
  }
  return contract.rateConversionStatus;
};

const getShownState = contract => {
  return contract.companyType === 'Contractor' ? transformBuyerState(contract) : transformSupplierState(contract);
};

const getTemplateStatus = doc => {
  const { hasSeenResponseWizard, state, isArchived } = doc;
  return R.call(R.cond([
    [() => hasSeenResponseWizard, R.always('draft')],
    [() => Boolean(state), R.always('new draft')],
    [() => isArchived, R.always('archived')],
    [R.T, R.always('completed')],
  ]));
};

const getTemplateStatusColor = R.cond([
  [equals('completed'), always('green')],
  [equals('draft'), always('#911ba8')],
  [equals('new draft'), always('#ffb700')],
  [equals('archived'), always('#005662')],
  [R.T, always('#005662')],
]);

const getStatusColor = cond([
  // [equals('downloaded'), always('#009688')],
  [anyPass([equals('viewed'), equals('pending')]), always('#ffb700')],
  [equals('accepted'), always('#31b5f7')],
  [equals('loading'), always('#3fa641')],
  [equals('loaded'), always('#33691e')],
  [equals('rejected'), always('#f5372a')],
  [equals('bounced'), always('red')],
  [R.equals('in progress'), R.always('#911ba8')],
  [R.equals('complete'), R.always('#7ac5ec')],
  [anyPass([equals('rates received'), equals('request')]), always('#ffb700')],
  [equals('sent'), always('#616061')],
  [equals('total'), always('#f9a8259c')],
  [equals('received-analytics'), always('#06a9bf')],
  [equals('accepted-analytics'), always('#7ac5ec')],
  [equals('loaded-analytics'), always('#69af90')],
  [T, always('#06a9bf')],
]);
const getPriorityColor = cond([
  [equals('Highest'), always('#b71c1c')],
  [equals('High'), always('#e53935')],
  [equals('Medium'), always('#f57c00')],
  [equals('Low'), always('#33691e')],
  [equals('Lowest'), always('#8bc34a')],
]);

const getPriorityIcon = cond([
  [equals('Highest'), always(<ArrowUpward color="#b71c1c" />)],
  [equals('High'), always(<ArrowUpward color="#e53935" />)],
  [equals('Medium'), always(<ArrowUpward color="#f57c00" />)],
  [equals('Low'), always(<ArrowDown color="#33691e"/>)],
  [equals('Lowest'), always(<ArrowDown color="#8bc34a"/>)],
]);

const getStatusIcon = cond([
  // [equals('downloaded'), always('tc-icon-archive')],
  [anyPass([equals('viewed'), equals('pending')]), always('tc-icon-av_timer')],
  [equals('accepted'), always('tc-icon-thumb_up')],
  [equals('loading'), always('tc-icon-watch_later')],
  [equals('loaded'), always('tc-icon-assignment_turned_in')],
  [equals('rejected'), always('tc-icon-thumb_down')],
  [anyPass([equals('draft'), equals('publish-ready'), equals('new draft')]), always('tc-icon-mode_edit')],
  [equals('sent'), always('tc-icon-paperplane')],
  [T, always('tc-icon-description')],
]);

const getIconEle = ({
  showEmailAndContractStatus = true,
  status: contractStatus,
  companyType,
  overwriteColor,
  otherSideRead,
  emailStatus,
  convertedFromExternal,
}) => {
  const addMailAndReadIcons = (largeIcon) => {
    const mailStatusIcon = (showEmailAndContractStatus) ? (() => {
      if (emailStatus === 0) return (<FontIcon className="tc-icon-more_vert" style={{ fontSize: '10px', color: 'rgba(0, 0, 0, 0.44)' }} />);
      if (emailStatus < 2) return (<FontIcon className="tc-icon-close" style={{ fontSize: '10px', color: 'red' }} />);
      if (emailStatus === 2) return (<FontIcon className="tc-icon-check" style={{ fontSize: '10px', color: 'green' }} />);
      if (emailStatus > 2) return (<FontIcon className="tc-icon-drafts" style={{ fontSize: '10px', color: 'rgba(0, 0, 0, 0.44)' }} />);
      return undefined;
    })() : undefined;
    const mailAndReadStatusIcons = (() => {
      if (otherSideRead && showEmailAndContractStatus) {
        return (
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            {mailStatusIcon}
            <FontIcon className="tc-icon-remove_red_eye" style={{ fontSize: '10px', color: 'rgba(0, 0, 0, 0.44)' }} />
          </div>
        );
      }
      return mailStatusIcon;
    })();
    return (
      <div className="flex-tc-sb">
        {mailAndReadStatusIcons}
        {largeIcon}
      </div>
    );
  };
  const getIconHover = (defaultText) => {
    const retVal = defaultText ? [defaultText] : [];
    if (showEmailAndContractStatus) {
      if (emailStatus === 0) retVal.push('Email Sending');
      if (emailStatus === 1) retVal.push('Email Bounced');
      if (emailStatus === 2) retVal.push('Email Delivered');
      if (emailStatus === 3) retVal.push('Email Opened');
      if (emailStatus === 4) retVal.push('Email Clicked');
      if (otherSideRead) retVal.push(`${(contractStatus === 'request') ? 'Request Viewed' : 'Contract Viewed/Downloaded'}`);
    }
    return retVal.join(', ');
  };
  const addIconHover = (largeIcon) => {
    if (!showEmailAndContractStatus) return largeIcon;
    return (
    <div
      className="hint--top"
      aria-label={getIconHover()}
    >
      {addMailAndReadIcons(largeIcon)}
    </div>);
  };
  const fontIconStyle = { marginLeft: '0.1em' };// the svg icons hav e a margin by default
  return R.cond([
    [R.equals('unpaid'), always(
      <WallWrapper slug="upgrade-rates-msgs" hideOnSkip blocker skipBack><div
        className="hint--top"
        aria-label={`Upgrade to view the status of your Sent ${companyType === 'Contractor' ? 'Requests' : 'Contracts'} (Bounced, Delivered, Opened)`}
      >
        <LockIcon style={{ fill: overwriteColor || '#FFD700', cursor: 'pointer' }} />
      </div></WallWrapper>
    )],
    [R.equals('sent'), always(addIconHover(<SentIcon color={overwriteColor || 'rgba(0, 0, 0, 0.44)'} />))],
    [R.equals('rates received externally'), always(
      <span
        className="hint--top"
        aria-label={getIconHover(convertedFromExternal ? 'TC Rate Conversion' : 'Non-Standard format')}
        style={{ width: '35px', display: 'inline-block' }}
      >
        {addMailAndReadIcons(
        <div style={{ position: 'relative' }}>
          {
            Boolean(convertedFromExternal) && (
              <RCICon
                style={{
                  position: 'absolute',
                  right: '13px',
                  top: '13px',
                  height: '15px',
                  width: '15px',
                  color: '#8f8f8f',
                }}
              />
            )
          }
          <RatesReceivedIcon color={overwriteColor || 'rgba(0, 0, 0, 0.44)'} />
          <span
            style={{
              position: 'absolute',
              display: 'inline-block',
              top: '13px',
              left: '13px',
              height: '12px',
              width: '12px',
              lineHeight: '12px',
              textAlign: 'center',
              fontSize: '10px',
              fontWeight: 'bold',
              borderRadius: '50%',
              backgroundColor: '#8f8f8f',
              color: 'white',
            }}
          >
            ?
          </span>
        </div>
        )}
      </span>
    )],
    [R.equals('rates received'), always(
      <span
        className="hint--top"
        aria-label={getIconHover(convertedFromExternal ? 'TC format converted by assignee' : 'TourConnect format')}
        style={{ width: '35px', display: 'inline-block' }}
      >
        {addMailAndReadIcons(
        <div style={{ position: 'relative' }}>
          {
            Boolean(convertedFromExternal) && (
              <RCICon
                style={{
                  position: 'absolute',
                  right: '13px',
                  top: '13px',
                  height: '15px',
                  width: '15px',
                  color: '#3fbbe5',
                }}
              />
            )
          }
          <RatesReceivedIcon color={overwriteColor || 'rgba(0, 0, 0, 0.44)'} />
          <img
            src={logoGlobeSrc}
            style={{
              position: 'absolute',
              left: '13px',
              top: '13px',
              height: '15px',
              width: '15px',
            }}
          />
        </div>)}
      </span>
    )],
    [R.equals('received'), always(<RatesReceivedIcon color={overwriteColor || 'rgba(0, 0, 0, 0.44)'} />)],
    [R.equals('viewed'), always(<FontIcon className="tc-icon-remove_red_eye" color={overwriteColor || '#00bcd4'} style={fontIconStyle} />)],
    [R.equals('accepted'), always(addIconHover(<FontIcon className="tc-icon-thumb_up" color={overwriteColor || '#31b5f7'} style={fontIconStyle} />))],
    [R.equals('loading'), always(addIconHover(<FontIcon className="tc-icon-watch_later" color={overwriteColor || '#3fa641'} style={fontIconStyle} />))],
    [R.equals('loaded'), always(addIconHover(<FontIcon className="tc-icon-assignment_turned_in" color={overwriteColor || '#33691e'} style={fontIconStyle} />))],
    [R.equals('rejected'), always(addIconHover(<FontIcon className="tc-icon-thumb_down" color={overwriteColor || '#f5372a'} style={fontIconStyle} />))],
    [R.equals('request'), always(addIconHover(<RequestIcon style={{ color: overwriteColor || '#ffb700' }} />))],
  ])(contractStatus);
};

const checkIfIsANewRequest = contract => !prop('locationId', contract);
const checkIfIsANewDraft = both(contract => has(prop('originFrom', contract), originFromMap), propEq('hasSeenResponseWizard', false));
const checkIfRatesReceived = both(propEq('state', 'pending'), contract => Boolean(prop('locationId', contract)));

const prettifyDays = days => {
  if (days && days.length > 0) {
    if (days.length === 7) return '';
    if (days.length === 1) return `${days[0]} Only`;
    const _getConsectuiveDays = () => {
      const _mapDaysToNumbers = (day) => {
        switch (day) {
          case 'Monday':
            return 1;
          case 'Tuesday':
            return 2;
          case 'Wednesday':
            return 3;
          case 'Thursday':
            return 4;
          case 'Friday':
            return 5;
          case 'Saturday':
            return 6;
          case 'Sunday':
            return 7;
        }
      };
      const _mapNumbersToDays = (num) => {
        switch (num) {
          case 1:
            return 'Mon';
          case 2:
            return 'Tue';
          case 3:
            return 'Wed';
          case 4:
            return 'Thu';
          case 5:
            return 'Fri';
          case 6:
            return 'Sat';
          case 7:
            return 'Sun';
        }
      };
      const dayNumbers = map(_mapDaysToNumbers, days).sort();
      var currentDay,
        previousDay,
        firstDay,
        lastDay;
      for (let i = 0; i < dayNumbers.length; i++) {
        currentDay = dayNumbers[i];
        if (!firstDay) firstDay = currentDay;
        lastDay = currentDay;
        if ((i > 0) && (i < dayNumbers.length)) {
          previousDay = dayNumbers[i - 1];
          if (currentDay - previousDay > 1) return false;
        }
      }
      return `${_mapNumbersToDays(firstDay)} - ${_mapNumbersToDays(lastDay)}`;
    };
    const _mapLongNameToShort = (long) => {
      switch (long) {
        case 'Monday':
          return 'Mon';
        case 'Tuesday':
          return 'Tue';
        case 'Wednesday':
          return 'Wed';
        case 'Thursday':
          return 'Thu';
        case 'Friday':
          return 'Fri';
        case 'Saturday':
          return 'Sat';
        case 'Sunday':
          return 'Sun';
      }
    };
    const consecutiveDays = _getConsectuiveDays(days);
    return consecutiveDays ? consecutiveDays : days.map(_mapLongNameToShort).join(', ');
  }
};
const getActionIndicatorColor = ({ viewingBy, ratesReceived, ratesLoaded }) => {
  if (ratesLoaded) return 'transparent';
  if (viewingBy === 'Supplier') {
    if (ratesReceived) return '#4db6ac';
    return '#ff6f00';
  }
  if (ratesReceived) return '#ff6f00';
  return '#4db6ac';
};

const stateValMap = {
  sent: 1,
  received: 2,
  accepted: 3,
  rejected: 3,
  loading: 3,
  loaded: 3,
};
const getSentState = (viewedFlag, emailStatus) => {
  if (viewedFlag || emailStatus > 2) return 'opened';
  if (emailStatus === 0) return 'sent';
  if (emailStatus === 2) return 'delivered';
  if (emailStatus > 2) return 'opened';
  return 'bounced';
};
const processEditHistories = (editHistory, productsArray) => {
  const productsMap = productsArray
  .reduce((result, product) => {
    result[product.productId] = product.productName;
    return result;
  }, {});
  const nonProductEditDetails = R.call(R.compose(
    R.join('|'),
    R.uniq,
    R.map(R.prop('detail')),
    R.reject(R.prop('productId'))
  ), editHistory);
  // filter edits for product that has been removed
  const productEdits = editHistory.filter(item => item.productId && productsMap[item.productId]);
  const groupedByProdNameNPackage = R.groupBy(
    ({ productId, ratePackageName }) => {
      const isSinglePackage = productsArray.filter(obj => obj.productId === productId).length < 2;
      return `${productsMap[productId]}${ratePackageName && !isSinglePackage ? ` - ${ratePackageName}` : ''}`;
    }
    , productEdits
  );
  return [
    {
      name: 'Policies & Notes',
      details: nonProductEditDetails,
    },
    ...R.keys(groupedByProdNameNPackage).map(name => {
      const edits = groupedByProdNameNPackage[name];
      return {
        name,
        details: R.call(R.compose(
          R.join('|'),
          R.uniq,
          R.map(R.prop('detail')),
        ), edits),
      };
    }),
  ].filter(R.prop('details'));
};

const checkIfDuplicateTemplatesSent = (contract, template) => {
  // not the save contracting period
  if (contract.startDate !== template.startDate || contract.endDate !== template.endDate) return false;
  if (contract.templateId === template._id) return true;
  if (
    contract.locationId === template.locationId &&
    // for some reason, some contract passed to this function have the marketInfo out of the supplierInfo
    // therefore adding ternary check to avoid breaking other processes
    (R.path(['supplierInfo', 'marketInfo'], contract) || contract.marketInfo || '') === (R.path(['supplierInfo', 'marketInfo'], template) || '')
  ) return true;
  return false;
};

const processPartnersForSendingRates = R.curry((templateSelected, templateGroupSelected, partner) => {
  // if email has account but wrong account type, treat it as external
  if (partner._type === 'Supplier' && !(partner.email && partner.email.indexOf('@') > -1)) return 'Cannot send to Supplier';
  const existingContracts = partner.contracts
  ? partner.contracts.filter(contract => {
    if (
      templateGroupSelected
      && templateGroupSelected.templates
      && templateGroupSelected.templates.length
    ) {
      return templateGroupSelected.templates
        .some(template => checkIfDuplicateTemplatesSent(contract, template));
    }
    return checkIfDuplicateTemplatesSent(contract, templateSelected);
  })
  : [];
  if (existingContracts.length) {
    // const ids = encodeURIComponent(JSON.stringify(existingContracts.map(obj => obj._id)));
    // return (
    //   <span>
    //   * Contract exists
    //     <a
    //       target="_blank"
    //       href={`/contracting/contract-list/search?ids=${ids}&resend=true`}
    //     >
    //       View & Resend
    //     </a>
    //   </span>);
    return '* Contract exists';
  }
});

export {
  getShownState,
  getSentState,
  getStatusColor,
  getStatusIcon,
  getIconEle,
  checkIfIsANewRequest,
  checkIfIsANewDraft,
  checkIfRatesReceived,
  prettifyDays,
  getActionIndicatorColor,
  stateValMap,
  processEditHistories,
  getTemplateStatusColor,
  getTemplateStatus,
  checkIfDuplicateTemplatesSent,
  processPartnersForSendingRates,
  getPriorityColor,
  getPriorityIcon,
};



// WEBPACK FOOTER //
// ./src/app/components/contracting/contractingUtils.jsx